import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import apolloClient from '@/lib/apollo-client';

import { getCookie } from '@/utils/cookies';
import { FILTERED_USERS_QUERY } from '@/lib/graphql/queries';
import { FilteredUsersResponse, UserFilter, UserState } from './types';




// Initial state
const initialState: UserState = {

  loading: false,
  error: null,
  isProfileLoaded: false,
  usersList: [],
  paginationMeta: null,
  usersLoading: false,
  usersError: null,
  filter: {
    search: "",
    roles: ["USER"],
    page: 1,
    limit: 10,
  }
};


// Async thunk for fetching filtered users
export const fetchFilteredUsers = createAsyncThunk(
  'user/fetchFilteredUsers',
  async (filter: UserFilter, { rejectWithValue }) => {
    try {
      const token = getCookie('token');
      const { data } = await apolloClient.query({
        query: FILTERED_USERS_QUERY,
        variables: {
          search: filter.search || null,
          roles: filter.roles || null,
          location: filter.location || null,
          page: filter.page || 1,
          limit: filter.limit || 10
        },
        context: {
          headers: {
            Authorization: `Bearer ${token}`
          }
        },
        fetchPolicy: 'network-only' // Don't use cache for user listing
      });
      
      // Validate the response structure
      if (!data?.filteredUsers?.users || !Array.isArray(data.filteredUsers.users)) {
        return rejectWithValue('Invalid response format from server');
      }
      
      return data.filteredUsers as FilteredUsersResponse;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch users');
    }
  }
);

// Create the slice
const userSlice = createSlice({
  name: 'user',
  initialState,

  reducers: {
    setUserFilter: (state, action: PayloadAction<UserFilter>) => {
      state.filter = {
        ...state.filter,
        ...action.payload
      };
    },
    resetUserFilter: (state) => {
      state.filter = initialState.filter;
    },
    clearUsersList: (state) => {
      state.usersList = [];
      state.paginationMeta = null;
    }
  },
  extraReducers: (builder) => {
    // Filtered users fetching cases
    builder
      .addCase(fetchFilteredUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchFilteredUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        state.usersList = action.payload.users;
        state.paginationMeta = action.payload.meta;
      })
      .addCase(fetchFilteredUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload as string;
      });
  }
});

// Export actions
export const { 
  setUserFilter,
  resetUserFilter,
  clearUsersList
} = userSlice.actions;

// Export reducer
export default userSlice.reducer;